import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useSEO } from '../utils/seo';
import './LegalPage.css';

const LAST_UPDATED = '6 March 2026';

const SECTIONS = [
  {
    id: 'overview',
    title: '1. Overview',
    content: `JSDEVBRAINS Digital Private Limited ("DevBrains", "we", "us") operates the DevBrains platform at devbrains.in. We are committed to protecting your personal data and being transparent about how we use it.

This Privacy Policy applies to all users of DevBrains — mentees, mentors, and visitors — and explains:
• What personal data we collect
• Why we collect it and how we use it
• How long we keep it
• Your rights over your data
• How to contact us

DevBrains is compliant with India's Digital Personal Data Protection Act 2023 (DPDPA), the EU General Data Protection Regulation (GDPR) for European users, and Malaysia's Personal Data Protection Act 2010 (PDPA) for Malaysian users.`,
  },
  {
    id: 'data-collected',
    title: '2. Data We Collect',
    content: `2.1 Account Data (collected at registration):
• Name, email address, phone number
• Profile photo (optional)
• Role (mentee or mentor)
• Country and time zone

2.2 Professional Data (mentors only):
• LinkedIn profile URL
• Current employer and job title
• Years of experience and skills
• Bank account / UPI details (for payouts)
• Aadhaar / PAN / Passport number (for KYC, encrypted and stored securely)

2.3 Session Data:
• Sessions booked, attended, and completed
• Session notes and chat messages
• Ratings and reviews given and received
• Duration, timing, and attendance records

2.4 Payment Data:
• Transaction IDs and amounts
• Payment method type (card/UPI/netbanking) — we do NOT store card numbers; these are held by Razorpay
• GST invoices and receipts
• TDS deduction records (mentors)

2.5 Usage Data (collected automatically):
• IP address and approximate location (country/city)
• Browser type, device type, operating system
• Pages visited and time spent
• Referral source (how you found us)
• Feature usage patterns

2.6 Communication Data:
• Emails sent to support@devbrains.in
• In-platform chat messages between mentors and mentees
• Feedback and survey responses

2.7 Identity Verification Data (mentors only):
• Government ID (Aadhaar/PAN/Passport) — used for KYC only, deleted after verification unless required by law`,
  },
  {
    id: 'why-we-collect',
    title: '3. Why We Collect Your Data',
    content: `We collect and process your data for the following lawful purposes:

3.1 Contract Performance — to provide the services you've signed up for:
• Creating and managing your account
• Facilitating bookings between mentors and mentees
• Processing payments and issuing refunds
• Generating invoices and TDS certificates

3.2 Legitimate Interests:
• Preventing fraud, abuse, and fake accounts
• Improving platform features and user experience
• Providing customer support
• Security monitoring and incident response

3.3 Legal Obligation:
• Maintaining financial records as required by Indian tax law
• KYC for mentor payouts as required by RBI regulations
• Responding to lawful government requests

3.4 Consent (where required):
• Sending marketing emails (you can opt out at any time)
• Using cookies for analytics and personalisation
• Sharing data with third-party analytics tools`,
  },
  {
    id: 'sharing',
    title: '4. How We Share Your Data',
    content: `4.1 With Mentors/Mentees: When you book a session, your name, profile photo, and booking details are shared with the other party. We do not share your email address or phone number without your consent.

4.2 Service Providers (processors):
• Razorpay — payment processing (PCI-DSS compliant)
• Amazon Web Services (AWS) — cloud hosting and storage
• SendGrid / Brevo — transactional email delivery
• Google Analytics — aggregated usage analytics (anonymised)
• DigiLocker / Hyperverge — KYC verification (mentors only)

4.3 Legal Requirements: We may disclose your data if required by law, court order, or government authority. We will notify you where legally permitted.

4.4 Business Transfer: In the event of a merger, acquisition, or sale of DevBrains, your data may be transferred. We will notify users with 30 days' notice before any such transfer.

4.5 We do NOT sell your personal data to advertisers or third-party data brokers. Ever.`,
  },
  {
    id: 'retention',
    title: '5. Data Retention',
    content: `5.1 Active account data is retained as long as your account is active.

5.2 After account deletion:
• Profile data is deleted within 30 days
• Session records and certificates are retained for 7 years (required by Indian accounting law)
• Payment and invoice records are retained for 7 years
• Chat messages are deleted within 90 days
• KYC documents are deleted immediately upon verification (unless legally required)

5.3 You can request data export before deleting your account from Dashboard → Settings → Export My Data.

5.4 Backup copies may persist for up to 90 days after deletion before being permanently purged from all systems.`,
  },
  {
    id: 'security',
    title: '6. Data Security',
    content: `6.1 We implement industry-standard security measures including:
• TLS 1.3 encryption for all data in transit
• AES-256 encryption for sensitive data at rest (KYC docs, 2FA secrets, payment data)
• Role-based access controls — only authorised staff can access user data
• Regular penetration testing and security audits
• Two-factor authentication (2FA) available for all user accounts

6.2 Our infrastructure is hosted on AWS (Mumbai, ap-south-1 region for Indian users) with daily encrypted backups.

6.3 Despite our best efforts, no internet transmission is 100% secure. If you become aware of a security issue, please report it immediately to security@devbrains.in.

6.4 In the event of a data breach affecting your rights and freedoms, we will notify you and the relevant data protection authority within 72 hours as required by applicable law.`,
  },
  {
    id: 'cookies',
    title: '7. Cookies & Tracking',
    content: `7.1 We use the following types of cookies:

Essential Cookies (cannot be disabled):
• Session authentication
• Security tokens
• Payment session management

Analytics Cookies (opt-out available):
• Google Analytics — aggregated page views and usage (anonymised)
• Hotjar — session recordings for UX improvement (no personal data)

Preference Cookies:
• Currency preference
• Language preference
• Dark/light mode setting

7.2 We do NOT use advertising cookies or share cookie data with ad networks.

7.3 You can manage cookies through your browser settings. Disabling essential cookies may prevent the platform from functioning correctly.`,
  },
  {
    id: 'rights',
    title: '8. Your Rights',
    content: `Under India's DPDPA 2023, GDPR (EU users), and PDPA (Malaysian users), you have the following rights:

✅ Right to Access — request a copy of all personal data we hold about you
✅ Right to Correction — correct inaccurate or incomplete data
✅ Right to Erasure — request deletion of your account and data (subject to legal retention requirements)
✅ Right to Data Portability — download your data in machine-readable format
✅ Right to Restrict Processing — limit how we use your data in certain circumstances
✅ Right to Object — object to processing based on legitimate interests
✅ Right to Withdraw Consent — opt out of marketing emails or analytics tracking at any time
✅ Right to Lodge a Complaint — complain to India's Data Protection Board, the EU supervisory authority (if applicable), or Malaysia's PDPC

To exercise any right, email privacy@devbrains.in. We will respond within 30 days.

To opt out of marketing emails: use the unsubscribe link in any email, or go to Dashboard → Settings → Notifications.`,
  },
  {
    id: 'children',
    title: '9. Children\'s Privacy',
    content: `DevBrains is not intended for users under 18 years of age. We do not knowingly collect personal data from minors. If we discover that a user is under 18, we will immediately delete their account and all associated data.

If you believe a minor has created an account on DevBrains, please contact privacy@devbrains.in immediately.`,
  },
  {
    id: 'international',
    title: '10. International Transfers',
    content: `DevBrains primarily stores data in AWS Mumbai (India). For users in Malaysia, Singapore, or the EU, data may be processed on servers in these regions via AWS or our service providers.

When transferring data outside India, we ensure appropriate safeguards are in place — including standard contractual clauses for GDPR compliance and equivalent protections for PDPA.

Our primary service providers are GDPR-certified and maintain equivalent data protection standards.`,
  },
  {
    id: 'changes',
    title: '11. Changes to This Policy',
    content: `We may update this Privacy Policy from time to time. When we make significant changes, we will:
• Update the "Last Updated" date at the top of this page
• Send an email notification to all registered users
• Post a notice in the platform dashboard

Your continued use of DevBrains after changes constitutes acceptance of the updated Privacy Policy.

For minor changes (grammar, clarifications that don't change the substance), we will update the policy without specific notification.`,
  },
  {
    id: 'contact',
    title: '12. Contact & Grievance',
    content: `For any privacy-related questions, requests, or complaints:

Privacy Team: privacy@devbrains.in
Data Protection Officer: dpo@devbrains.in

Grievance Officer (as required under India's IT Act):
JSDEVBRAINS Digital Private Limited
Bengaluru, Karnataka, India – 560001
grievance@devbrains.in
Response time: Within 24 hours of receipt

EU Representative (GDPR Article 27): Available on request at privacy@devbrains.in

If you are not satisfied with our response, you may escalate to:
• India: Data Protection Board of India (once operational)
• EU: Your local data protection supervisory authority
• Malaysia: Personal Data Protection Commissioner (PDPC)`,
  },
];

export default function PrivacyPage({ onSignIn }) {
  const [active, setActive] = useState('overview');
  useSEO({
    title: 'Privacy Policy',
    description: 'DevBrains Privacy Policy — how we collect, use, and protect your personal data. GDPR, DPDPA, and PDPA compliant.',
    canonical: '/privacy',
  });

  return (
    <>
      <Navbar onSignIn={onSignIn} />
      <div className="legal-page">

        {/* Hero */}
        <div className="legal-hero privacy-hero">
          <div className="legal-hero-inner">
            <div className="legal-hero-badge">🔒 Privacy</div>
            <h1 className="legal-hero-title">Privacy Policy</h1>
            <p className="legal-hero-sub">
              Last updated: <strong>{LAST_UPDATED}</strong> &nbsp;·&nbsp;
              DPDPA 2023 · GDPR · PDPA Compliant
            </p>
            <div className="legal-hero-chips">
              <span>🚫 We never sell your data</span>
              <span>🔐 AES-256 encrypted</span>
              <span>🌍 Global standards</span>
            </div>
            <div className="legal-quick-nav">
              <Link to="/terms" className="legal-quick-link">📋 Terms &amp; Conditions →</Link>
              <a href="mailto:privacy@devbrains.in" className="legal-quick-link">✉️ Contact Privacy Team →</a>
            </div>
          </div>
        </div>

        <div className="legal-layout">
          {/* Sidebar TOC */}
          <aside className="legal-toc">
            <div className="legal-toc-label">Contents</div>
            {SECTIONS.map(s => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className={`legal-toc-item ${active === s.id ? 'active' : ''}`}
                onClick={() => setActive(s.id)}
              >
                {s.title}
              </a>
            ))}
            {/* Quick rights box */}
            <div className="legal-toc-rights">
              <div className="legal-toc-rights-title">Your Rights</div>
              <div className="legal-toc-rights-list">
                {['Access', 'Correct', 'Delete', 'Export', 'Object'].map(r => (
                  <span key={r} className="legal-toc-right-tag">✓ {r}</span>
                ))}
              </div>
              <a href="mailto:privacy@devbrains.in" className="legal-toc-rights-cta">
                Exercise a Right →
              </a>
            </div>
          </aside>

          {/* Content */}
          <div className="legal-content">
            <div className="legal-notice privacy-notice">
              <span className="legal-notice-icon">🛡️</span>
              <span><strong>Our commitment:</strong> DevBrains will never sell your personal data to advertisers. Your data is used only to provide and improve the services you signed up for.</span>
            </div>

            {SECTIONS.map(s => (
              <section key={s.id} id={s.id} className="legal-section">
                <h2 className="legal-section-title">{s.title}</h2>
                {s.content.split('\n\n').map((para, i) => (
                  <p key={i} className="legal-para">{para}</p>
                ))}
              </section>
            ))}

            <div className="legal-footer-note">
              <p>Questions? Email <a href="mailto:privacy@devbrains.in">privacy@devbrains.in</a> — we respond within 24 hours.</p>
              <div className="legal-also">
                <Link to="/terms">Terms &amp; Conditions →</Link>
                <Link to="/blog">Help Centre →</Link>
                <Link to="/mentors">Find a Mentor →</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
